import React, { useState, useEffect } from "react";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import {
  FaRunning,
  FaSearch,
  FaIdCard,
  FaPhoneAlt,
  FaFilePdf,
  FaEye,
  FaUpload,
  FaTimes,
  FaSave,
} from "react-icons/fa";
import Swal from "sweetalert2";
import FileUploadWithScanner from "../../components/common/FileUploadWithScanner";

const CoachAthletes = () => {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState("health_report");
  const [uploadDescription, setUploadDescription] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Multi-Sport Allotment States (Max 3 - Performance Based)
  const [allotSportsModalOpen, setAllotSportsModalOpen] = useState(false);
  const [selectedAthleteForSports, setSelectedAthleteForSports] = useState(null);
  const [sportsForm, setSportsForm] = useState({ disciplines: [], notes: "" });
  const [campDisciplines, setCampDisciplines] = useState([]);
  const [savingSports, setSavingSports] = useState(false);

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const res = await API.camp.coachPortal.getMyAthletes();
      if (res?.data?.athletes || res?.athletes) {
        setAthletes(res?.data?.athletes || res?.athletes);
      }
    } catch (err) {
      console.error("Failed to load assigned athletes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAthletes();
    API.camp.getSettings()
      .then((res) => {
        const list = res?.data?.disciplines || res?.disciplines || [];
        setCampDisciplines(list);
      })
      .catch((e) => console.warn("Failed to load camp disciplines:", e));
  }, []);

  const handleOpenSportsModal = (athlete) => {
    setSelectedAthleteForSports(athlete);
    let initialDisciplines = [];
    if (Array.isArray(athlete.allocated_disciplines) && athlete.allocated_disciplines.length > 0) {
      initialDisciplines = athlete.allocated_disciplines.map((d) =>
        typeof d === "string" ? d : d.discipline_name || d.name || ""
      ).filter(Boolean);
    } else if (athlete.discipline_name) {
      initialDisciplines = [athlete.discipline_name];
    }
    setSportsForm({
      disciplines: initialDisciplines.slice(0, 3),
      notes: athlete.sports_allotment_notes || athlete.performance_notes || "",
    });
    setAllotSportsModalOpen(true);
  };

  const handleSaveSportsAllotment = async (e) => {
    e.preventDefault();
    if (!selectedAthleteForSports) return;
    if (!sportsForm.disciplines || sportsForm.disciplines.length === 0) {
      Swal.fire("Validation", "Please select at least 1 sport discipline (up to 3).", "warning");
      return;
    }
    setSavingSports(true);
    try {
      await API.camp.coachPortal.allotSports(selectedAthleteForSports.id, {
        disciplines: sportsForm.disciplines,
        notes: sportsForm.notes,
      });
      Swal.fire({
        icon: "success",
        title: "Sports Allotted Successfully!",
        text: `Allocated ${sportsForm.disciplines.length} sport(s) to ${selectedAthleteForSports.name} based on performance evaluation.`,
        timer: 2000,
        showConfirmButton: false,
      });
      setAllotSportsModalOpen(false);
      setSelectedAthleteForSports(null);
      fetchAthletes();
    } catch (err) {
      console.error("Failed to allot sports:", err);
      Swal.fire("Error", err?.response?.data?.message || err?.message || "Failed to allot sports", "error");
    } finally {
      setSavingSports(false);
    }
  };

  const filtered = athletes.filter((a) => {
    const term = searchTerm.toLowerCase();
    return (
      a.name?.toLowerCase().includes(term) ||
      a.bib_no?.toLowerCase().includes(term) ||
      a.category?.toLowerCase().includes(term) ||
      a.discipline_name?.toLowerCase().includes(term) ||
      a.district?.toLowerCase().includes(term)
    );
  });

  const handleDocumentUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      Swal.fire("Error", "Please select a file to upload", "warning");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("athlete_id", selectedAthlete.id);
    formData.append("document_type", uploadDocType);
    formData.append("description", uploadDescription);
    formData.append("file", uploadFile);

    try {
      const res = await API.camp.coachPortal.uploadDocument(formData);
      if (res?.data?.success || res?.success) {
        Swal.fire("Success", "Document uploaded successfully", "success");
        setUploadModalOpen(false);
        setUploadFile(null);
        setUploadDescription("");

        // Refresh selected athlete profile
        const updated = await API.camp.coachPortal.getAthlete(selectedAthlete.id);
        if (updated?.data?.athlete || updated?.athlete) {
          setSelectedAthlete(updated?.data?.athlete || updated?.athlete);
        }
      }
    } catch (err) {
      Swal.fire("Error", err?.message || "Failed to upload document", "error");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Search Banner */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Athlete Registry</p>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-0.5 tracking-tight flex items-center gap-2">
              <FaRunning className="text-cyan-700" /> My Assigned Athletes
            </h1>
            <p className="text-xs text-slate-600 mt-1">
              Athletes assigned to your coaching care for this training camp ({athletes.length} total)
            </p>
          </div>

          <div className="relative w-full sm:w-72">
            <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search name, BIB, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Athletes Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-2xl shadow-sm">
          <FaRunning className="text-slate-400 text-4xl mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">No athletes found</p>
          <p className="text-xs text-slate-500 mt-1">
            {searchTerm ? "Try searching with a different term" : "No athletes mapped to your profile yet"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((athlete) => {
            const photoUrl = resolveAssetUrl(athlete.photo, {
              defaultPath: "/images/default-user.png",
            });

            return (
              <div
                key={athlete.id}
                className="bg-white border border-slate-200 hover:border-cyan-400 rounded-2xl p-5 shadow-sm flex flex-col justify-between transition-all group"
              >
                <div>
                  <div className="flex items-start gap-3">
                    <img
                      src={photoUrl}
                      alt={athlete.name}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-user.png";
                      }}
                      className="w-14 h-14 rounded-xl object-cover border border-slate-200 bg-slate-50 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between flex-wrap gap-1">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-800 border border-cyan-200">
                          ID: {athlete.id_number || athlete.bib_no}
                        </span>
                        <div className="flex items-center gap-1">
                          {athlete.room_no && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                              Rm {athlete.room_no}
                            </span>
                          )}
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                            {athlete.category}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 mt-1 truncate">
                        {athlete.name}
                      </h3>
                      <p className="text-xs text-slate-500 truncate">
                        Father: {athlete.father_name || "N/A"}
                      </p>
                      {(athlete.medicines || athlete.health_issues) && (
                        <div className="mt-1">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200 inline-flex items-center gap-1">
                            💊 Prescribed Meds
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 text-xs">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-400 text-[10px] font-semibold uppercase">Allotted Sports (Max 3)</span>
                        <button
                          type="button"
                          onClick={() => handleOpenSportsModal(athlete)}
                          className="text-[10px] font-bold text-cyan-700 hover:text-cyan-900 flex items-center gap-1"
                          title="Allot or change sports based on performance"
                        >
                          <FaRunning className="text-[9px]" /> Allot / Change
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(athlete.allocated_disciplines) && athlete.allocated_disciplines.length > 0
                          ? athlete.allocated_disciplines
                          : [athlete.discipline_name].filter(Boolean)
                        ).map((disp, dIdx) => {
                          const dName = typeof disp === "string" ? disp : disp.discipline_name || disp.name;
                          return (
                            <span
                              key={dIdx}
                              className={`px-2 py-0.5 rounded-md font-bold text-[10px] border ${
                                dIdx === 0
                                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : dIdx === 1
                                  ? "bg-cyan-50 text-cyan-800 border-cyan-200"
                                  : "bg-indigo-50 text-indigo-800 border-indigo-200"
                              }`}
                            >
                              <span className="opacity-70 mr-0.5 text-[9px]">#{dIdx + 1}</span>
                              {dName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-50 text-slate-500 text-[11px]">
                      <span>District: <strong className="text-slate-700">{athlete.district || "N/A"}</strong></span>
                      {athlete.id_number && <span>ID: <strong className="font-mono text-slate-700">{athlete.id_number}</strong></span>}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-xs text-slate-500 flex items-center gap-1 font-medium">
                    <FaPhoneAlt className="text-[10px] text-slate-400" />
                    {athlete.parent_mobile || "N/A"}
                  </span>

                  <button
                    onClick={() => setSelectedAthlete(athlete)}
                    className="px-3 py-1.5 bg-cyan-50 hover:bg-cyan-700 text-cyan-800 hover:text-white border border-cyan-200 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                  >
                    <FaEye /> Profile & Docs
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Athlete Profile & Documents Modal */}
      {selectedAthlete && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl text-slate-900">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-2">
                <FaIdCard className="text-cyan-700 text-lg" />
                <h2 className="text-base font-bold text-slate-900">Athlete Details & Records</h2>
              </div>
              <button
                onClick={() => setSelectedAthlete(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Top Banner */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                <img
                  src={resolveAssetUrl(selectedAthlete.photo, {
                    defaultPath: "/images/default-user.png",
                  })}
                  alt={selectedAthlete.name}
                  className="w-16 h-16 rounded-xl object-cover bg-white border border-slate-200 shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-bold text-slate-900">{selectedAthlete.name}</h3>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-800 font-bold border border-cyan-200">
                      BIB: {selectedAthlete.bib_no}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Category: <span className="font-semibold text-amber-700">{selectedAthlete.category}</span> &bull;{" "}
                    Discipline: <span className="font-semibold text-emerald-700">{selectedAthlete.discipline_name}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    District: {selectedAthlete.district} &bull; Father: {selectedAthlete.father_name}
                  </p>
                </div>
              </div>

              {/* Personal Information Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 bg-slate-50/60 p-4 rounded-2xl border border-slate-200 text-xs">
                <div>
                  <span className="text-slate-500 block font-semibold">Parent Contact</span>
                  <span className="font-semibold text-slate-900">{selectedAthlete.parent_mobile}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-semibold">Aadhaar No.</span>
                  <span className="font-semibold text-slate-900">{selectedAthlete.aadhaar || "Not recorded"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-semibold">UDID Number</span>
                  <span className="font-semibold text-slate-900">{selectedAthlete.udid || "N/A"}</span>
                </div>
                <div>
                  <span className="text-slate-500 block font-semibold">UDISE No.</span>
                  <span className="font-semibold text-slate-900">{selectedAthlete.udise_no || "N/A"}</span>
                </div>
                <div className="col-span-2">
                  <span className="text-slate-500 block font-semibold">Address</span>
                  <span className="font-semibold text-slate-900">{selectedAthlete.address || "N/A"}</span>
                </div>
              </div>

              {/* Allotted Sports Section (Upto 3 Sports) */}
              <div className="p-4 rounded-2xl border border-emerald-200 bg-emerald-50/50 space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🏃</span>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-900">
                      Allotted Sports Disciplines (Upto 3)
                    </h4>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const ath = selectedAthlete;
                      setSelectedAthlete(null);
                      handleOpenSportsModal(ath);
                    }}
                    className="px-2.5 py-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-[10px] flex items-center gap-1 shadow-sm transition"
                  >
                    <FaRunning className="text-[9px]" /> Allot / Change Sports
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {(Array.isArray(selectedAthlete.allocated_disciplines) && selectedAthlete.allocated_disciplines.length > 0
                    ? selectedAthlete.allocated_disciplines
                    : [selectedAthlete.discipline_name].filter(Boolean)
                  ).map((disp, dIdx) => {
                    const dName = typeof disp === "string" ? disp : disp.discipline_name || disp.name;
                    return (
                      <div key={dIdx} className="px-3 py-1.5 rounded-xl bg-white border border-emerald-300 font-bold text-xs text-slate-800 flex items-center gap-1.5 shadow-2xs">
                        <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-mono">
                          Slot #{dIdx + 1}
                        </span>
                        <span>{dName}</span>
                      </div>
                    );
                  })}
                </div>
                {(selectedAthlete.sports_allotment_notes || selectedAthlete.performance_notes) && (
                  <div className="text-xs text-slate-600 bg-white/80 p-2.5 rounded-xl border border-emerald-100 mt-1">
                    <strong className="text-slate-700">Performance Notes / Rationale:</strong> {selectedAthlete.sports_allotment_notes || selectedAthlete.performance_notes}
                  </div>
                )}
              </div>

              {/* Room Allotment & Medical Schedule */}
              <div className="p-4 rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50/60 via-white to-amber-50/40 space-y-3">
                <div className="flex items-center justify-between border-b border-rose-100 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-base">🏥</span>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-rose-900">
                      Room Allotment & Medical Care
                    </h4>
                  </div>
                  {selectedAthlete.room_no ? (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-100 text-indigo-800 border border-indigo-200">
                      Allotted: Room {selectedAthlete.room_no}
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      Room not allotted
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <div className="p-2.5 bg-white/80 rounded-xl border border-rose-100">
                    <span className="text-slate-500 block text-[11px] font-semibold">Health Issues / Conditions</span>
                    <span className="text-slate-900 font-medium mt-0.5 block">
                      {selectedAthlete.health_issues || "None reported"}
                    </span>
                  </div>

                  <div className="p-2.5 bg-white/80 rounded-xl border border-rose-100">
                    <span className="text-slate-500 block text-[11px] font-semibold">Emergency Contact</span>
                    <span className="text-slate-900 font-medium mt-0.5 block">
                      {selectedAthlete.emergency_contact || selectedAthlete.parent_mobile || "N/A"}
                    </span>
                  </div>

                  <div className="p-2.5 bg-white/80 rounded-xl border border-rose-100 sm:col-span-2">
                    <span className="text-slate-500 block text-[11px] font-semibold">Prescribed Medicines</span>
                    <span className="text-rose-700 font-semibold mt-0.5 block">
                      {selectedAthlete.medicines ? (
                        <span className="px-2 py-0.5 rounded bg-rose-50 border border-rose-200 inline-block">
                          💊 {selectedAthlete.medicines}
                        </span>
                      ) : (
                        <span className="text-slate-500 font-normal">No regular medicines prescribed</span>
                      )}
                    </span>
                  </div>

                  <div className="p-2.5 bg-white/80 rounded-xl border border-rose-100 sm:col-span-2">
                    <span className="text-slate-500 block text-[11px] font-semibold">Medicine Timings</span>
                    <span className="text-emerald-800 font-semibold mt-0.5 block">
                      {selectedAthlete.medicine_timings ? (
                        <span className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 inline-block">
                          ⏰ {selectedAthlete.medicine_timings}
                        </span>
                      ) : (
                        <span className="text-slate-500 font-normal">N/A</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents List */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                    <FaFilePdf className="text-amber-600" /> Attached Documents & Certificates
                  </h4>
                  <button
                    onClick={() => setUploadModalOpen(true)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1 shadow-sm"
                  >
                    <FaUpload className="text-[10px]" /> Upload Report
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Registration Core Docs */}
                  {selectedAthlete.aadhaar_doc && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                      <div>
                        <p className="font-semibold text-slate-800">Aadhaar Card Document</p>
                        <p className="text-[10px] text-slate-500">Registration Document</p>
                      </div>
                      <a
                        href={resolveAssetUrl(selectedAthlete.aadhaar_doc)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1 bg-cyan-700 text-white hover:bg-cyan-800 rounded-lg text-xs font-semibold shadow-sm"
                      >
                        View Document
                      </a>
                    </div>
                  )}

                  {selectedAthlete.udid_doc && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                      <div>
                        <p className="font-semibold text-slate-800">UDID Certificate</p>
                        <p className="text-[10px] text-slate-500">Registration Document</p>
                      </div>
                      <a
                        href={resolveAssetUrl(selectedAthlete.udid_doc)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1 bg-cyan-700 text-white hover:bg-cyan-800 rounded-lg text-xs font-semibold shadow-sm"
                      >
                        View Document
                      </a>
                    </div>
                  )}

                  {selectedAthlete.sadarem_doc && (
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                      <div>
                        <p className="font-semibold text-slate-800">SADAREM Certificate</p>
                        <p className="text-[10px] text-slate-500">Government Disability Certificate</p>
                      </div>
                      <a
                        href={resolveAssetUrl(selectedAthlete.sadarem_doc)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1 bg-cyan-700 text-white hover:bg-cyan-800 rounded-lg text-xs font-semibold shadow-sm"
                      >
                        View Document
                      </a>
                    </div>
                  )}

                  {/* Additional / Ongoing Documents */}
                  {selectedAthlete.documents?.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs"
                    >
                      <div>
                        <p className="font-semibold text-slate-800 capitalize">
                          {doc.document_type?.replace(/_/g, " ")}
                        </p>
                        <p className="text-[10px] text-slate-500">
                          {doc.description || doc.file_name} &bull; {doc.created_at?.slice(0, 10)}
                        </p>
                      </div>
                      <a
                        href={resolveAssetUrl(doc.file_path)}
                        target="_blank"
                        rel="noreferrer"
                        className="px-3 py-1 bg-cyan-700 text-white hover:bg-cyan-800 rounded-lg text-xs font-semibold shadow-sm"
                      >
                        Download / View
                      </a>
                    </div>
                  ))}

                  {!selectedAthlete.aadhaar_doc &&
                    !selectedAthlete.udid_doc &&
                    !selectedAthlete.sadarem_doc &&
                    (!selectedAthlete.documents || selectedAthlete.documents.length === 0) && (
                      <p className="text-xs text-slate-500 py-3 text-center">
                        No additional documents uploaded yet.
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4 text-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <h3 className="text-base font-bold text-slate-900">Upload Athlete Report / Document</h3>
              <button
                onClick={() => setUploadModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleDocumentUpload} className="space-y-4 text-xs">
              <div>
                <label className="block text-slate-700 font-semibold mb-1">Document Type</label>
                <select
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                >
                  <option value="health_report">Health Report</option>
                  <option value="progress_card">Progress Card</option>
                  <option value="weekly_report">Weekly Report</option>
                  <option value="coaching_camp_report">Coaching Camp Report</option>
                  <option value="other">Other Document</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-700 font-semibold mb-1">Description / Notes</label>
                <input
                  type="text"
                  placeholder="e.g. Week 2 medical fitness report"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>

              <div>
                <FileUploadWithScanner
                  label="Select File (PDF/Image)"
                  name="uploadFile"
                  accept=".pdf,.jpg,.jpeg,.png"
                  required
                  value={uploadFile}
                  onChange={(file) => setUploadFile(file)}
                  scannerTitle="Scan Athlete Document"
                  targetFileName={`athlete_doc_${selectedAthlete?.id || "doc"}_${Date.now()}.jpg`}
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setUploadModalOpen(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-xl font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold flex items-center gap-1.5 disabled:opacity-50 shadow-sm transition"
                >
                  {uploading ? "Uploading..." : "Save Document"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Allot / Change Sports Modal (Max 3 - Performance Based) */}
      {allotSportsModalOpen && selectedAthleteForSports && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-5 relative z-10 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <FaRunning className="text-base" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">
                    Allot / Change Sports (Max 3)
                  </h3>
                  <p className="text-xs text-slate-500">
                    Allot up to 3 specific sports or update based on athlete performance
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setAllotSportsModalOpen(false);
                  setSelectedAthleteForSports(null);
                }}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Athlete Summary */}
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img
                  src={resolveAssetUrl(selectedAthleteForSports.photo, {
                    defaultPath: "/images/default-user.png",
                  })}
                  alt={selectedAthleteForSports.name}
                  className="w-12 h-12 rounded-xl object-cover border border-slate-200 bg-white"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/default-user.png";
                  }}
                />
                <div>
                  <div className="font-bold text-slate-900 text-sm">
                    {selectedAthleteForSports.name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    District: {selectedAthleteForSports.district} &bull; Class: <span className="font-bold text-indigo-700">{selectedAthleteForSports.category || "General"}</span>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">ID / BIB No</div>
                <div className="font-mono font-bold text-cyan-900 text-sm bg-cyan-100/70 border border-cyan-200 px-2.5 py-0.5 rounded-lg inline-block">
                  {selectedAthleteForSports.id_number || selectedAthleteForSports.bib_no || "N/A"}
                </div>
              </div>
            </div>

            <form onSubmit={handleSaveSportsAllotment} className="space-y-4 text-xs">
              {/* Sports Selection Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-slate-700 font-bold uppercase tracking-wider text-xs">
                    Allotted Sport Disciplines (Select 1 to 3) *
                  </label>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    sportsForm.disciplines.length === 3
                      ? "bg-amber-50 text-amber-800 border-amber-300"
                      : "bg-emerald-50 text-emerald-800 border-emerald-300"
                  }`}>
                    {sportsForm.disciplines.length} / 3 Allotted
                  </span>
                </div>

                <div className="space-y-2">
                  {[0, 1, 2].map((slotIdx) => {
                    const slotLabel = slotIdx === 0 ? "Primary Sport (1st) *" : slotIdx === 1 ? "Secondary Sport (2nd - Optional)" : "Tertiary Sport (3rd - Optional)";
                    const currentValue = sportsForm.disciplines[slotIdx] || "";

                    return (
                      <div key={slotIdx} className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl space-y-1">
                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-600">
                          <span>{slotLabel}</span>
                          {currentValue && (
                            <button
                              type="button"
                              onClick={() => {
                                const updated = sportsForm.disciplines.filter((_, idx) => idx !== slotIdx);
                                setSportsForm({ ...sportsForm, disciplines: updated });
                              }}
                              className="text-[10px] text-rose-600 hover:text-rose-800 font-semibold flex items-center gap-1"
                            >
                              <FaTimes className="text-[9px]" /> Remove
                            </button>
                          )}
                        </div>
                        <select
                          value={currentValue}
                          onChange={(e) => {
                            const chosen = e.target.value;
                            const updated = [...sportsForm.disciplines];
                            if (chosen) {
                              if (updated.includes(chosen) && updated[slotIdx] !== chosen) {
                                Swal.fire("Notice", "This sport is already allotted to another slot.", "info");
                                return;
                              }
                              updated[slotIdx] = chosen;
                            } else {
                              updated.splice(slotIdx, 1);
                            }
                            setSportsForm({
                              ...sportsForm,
                              disciplines: updated.filter(Boolean).slice(0, 3),
                            });
                          }}
                          className="w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs text-slate-900 font-medium focus:ring-2 focus:ring-cyan-500"
                        >
                          <option value="">-- {slotIdx === 0 ? "Select Primary Sport *" : "None (Optional)"} --</option>
                          {campDisciplines.map((d) => (
                            <option key={d.id} value={d.name}>
                              {d.name} {d.code ? `(${d.code})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  Athletes can be tested and allotted up to 3 sports during the camp based on performance evaluations.
                </p>
              </div>

              {/* Performance Notes / Rationale */}
              <div>
                <label className="block text-slate-700 font-bold mb-1 uppercase tracking-wider text-xs">
                  Performance Notes / Allotment Reason
                </label>
                <textarea
                  rows={3}
                  placeholder="Enter observations based on physical tests, trials, stamina, speed, or performance changes..."
                  value={sportsForm.notes}
                  onChange={(e) => setSportsForm({ ...sportsForm, notes: e.target.value })}
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-2.5 text-slate-900 text-xs focus:ring-2 focus:ring-cyan-500 font-medium"
                />
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Record trial scores, observations, or rationale for altering or adding sports.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setAllotSportsModalOpen(false);
                    setSelectedAthleteForSports(null);
                  }}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingSports}
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold shadow-md shadow-emerald-600/20 transition-all flex items-center gap-1.5 disabled:opacity-50"
                >
                  <FaSave /> {savingSports ? "Saving Allotment..." : "Save Sports Allotment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachAthletes;
